import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getSubscriptionAnalytics, exportAdminData, adminAnalyticsAPI, authUtils } from '../api/userAPI';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const SubscriptionAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [usageAnalytics, setUsageAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [dateRange, setDateRange] = useState('30d');
  const [modelTypeFilter, setModelTypeFilter] = useState('all');

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSubscriptionAnalytics();
      setAnalytics(data);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsageAnalytics = useCallback(async () => {
    try {
      setUsageLoading(true);
      setError(null);
      const token = authUtils.getToken();
      const filters = {
        range: dateRange,
        model_type: modelTypeFilter
      };
      const data = await adminAnalyticsAPI.getAPIUsageAnalytics(filters, token);
      setUsageAnalytics(data);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load usage analytics:', err);
    } finally {
      setUsageLoading(false);
    }
  }, [dateRange, modelTypeFilter]);

  useEffect(() => {
    loadAnalytics();
  }, []);

  useEffect(() => {
    loadUsageAnalytics();
  }, [loadUsageAnalytics]);

  const handleExport = async (format) => {
    try {
      setExporting(true);
      await exportAdminData(format);
    } catch (err) {
      setError(err.message);
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="subscription-analytics">
        <div className="loading">Loading subscription analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-analytics">
        <div className="error">
          <h3>Error Loading Analytics</h3>
          <p>{error}</p>
          <button onClick={loadAnalytics} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="subscription-analytics">
        <div className="no-data">No analytics data available</div>
      </div>
    );
  }

  return (
    <div className="subscription-analytics">
      <div className="analytics-header">
        <h2>Subscription Analytics</h2>
        <div className="header-actions">
          <button 
            onClick={loadAnalytics} 
            className="refresh-button"
            disabled={loading}
          >
            Refresh
          </button>
          <div className="export-buttons">
            <button 
              onClick={() => handleExport('csv')} 
              className="export-button csv"
              disabled={exporting}
            >
              Export CSV
            </button>
            <button 
              onClick={() => handleExport('json')} 
              className="export-button json"
              disabled={exporting}
            >
              Export JSON
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="metrics-grid">
        <div className="metric-card total-subscribers">
          <div className="metric-content">
            <h3>Total Subscribers</h3>
            <div className="metric-value">{analytics.total_subscribers || 0}</div>
            <div className="metric-subtitle">
              {analytics.active_subscribers || 0} active
            </div>
          </div>
        </div>

        <div className="metric-card estimated-mrr">
          <div className="metric-content">
            <h3>Estimated MRR</h3>
            <div className="metric-value">{formatCurrency(analytics.estimated_mrr || 0)}</div>
            <div className="metric-subtitle">Monthly Recurring Revenue</div>
          </div>
        </div>

        <div className="metric-card active-subscribers">
          <div className="metric-content">
            <h3>Active Subscribers</h3>
            <div className="metric-value">{analytics.active_subscribers || 0}</div>
            <div className="metric-subtitle">
              {formatPercentage(((analytics.active_subscribers || 0) / (analytics.total_subscribers || 1)) * 100)} of total
            </div>
          </div>
        </div>

        <div className="metric-card enterprise-count">
          <div className="metric-content">
            <h3>Enterprise Users</h3>
            <div className="metric-value">{analytics.plan_distribution?.enterprise?.count || 0}</div>
            <div className="metric-subtitle">
              {formatPercentage(analytics.plan_distribution?.enterprise?.percentage || 0)} of total
            </div>
          </div>
        </div>
      </div>

      {/* Plan Distribution */}
      <div className="analytics-section">
        <h3>Plan Distribution</h3>
        <div className="plan-distribution">
          <div className="plan-chart">
            {Object.entries(analytics.plan_distribution || {}).map(([plan, data]) => (
              <div key={plan} className="plan-bar">
                <div className="plan-label">
                  <span className="plan-name">{plan.charAt(0).toUpperCase() + plan.slice(1)}</span>
                  <span className="plan-count">{data?.count || 0}</span>
                </div>
                <div className="plan-progress">
                  <div 
                    className="plan-fill"
                    style={{ width: `${data?.percentage || 0}%` }}
                    data-percentage={data?.percentage || 0}
                  ></div>
                </div>
                <div className="plan-percentage">{formatPercentage(data?.percentage || 0)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="analytics-section">
        <h3>Revenue Breakdown</h3>
        <div className="revenue-breakdown">
          <div className="revenue-chart">
            {Object.entries(analytics.revenue_breakdown || {}).map(([plan, amount]) => (
              <div key={plan} className="revenue-item">
                <div className="revenue-plan">
                  {plan.charAt(0).toUpperCase() + plan.slice(1)} Plan
                </div>
                <div className="revenue-amount">
                  {formatCurrency(amount || 0)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="analytics-section">
        <h3>Status Breakdown</h3>
        <div className="status-breakdown">
          <div className="status-grid">
            {Object.entries(analytics.status_breakdown || {}).map(([status, count]) => (
              <div key={status} className="status-item">
                <div className="status-name">
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </div>
                <div className="status-count">{count || 0}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* API Usage Analytics Section */}
      <div className="analytics-section usage-analytics">
        <h2>API Usage Analytics</h2>
        
        {/* Filters */}
        <div className="filters-section">
          <div className="filter-group">
            <label htmlFor="date-range">Date Range:</label>
            <select 
              id="date-range"
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)}
              className="filter-select"
            >
              <option value="24h">24 Hours</option>
              <option value="7d">7 Days</option>
              <option value="30d">30 Days</option>
              <option value="90d">90 Days</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="model-type">Model Type:</label>
            <select 
              id="model-type"
              value={modelTypeFilter} 
              onChange={(e) => setModelTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Types</option>
              <option value="classical">Classical</option>
              <option value="neural">Neural</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>

          <button 
            onClick={loadUsageAnalytics} 
            className="refresh-button"
            disabled={usageLoading}
          >
            {usageLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {usageLoading && (
          <div className="loading">Loading API usage analytics...</div>
        )}

        {usageAnalytics && !usageLoading && (
          <>
            {/* P99 Statistics */}
            <div className="p99-stats-section">
              <h3>P99 Statistics</h3>
              <div className="p99-cards">
                <div className="p99-card">
                  <h4>P99 Usage per User</h4>
                  <div className="p99-value">{Math.round(usageAnalytics.p99_stats?.usage_p99_per_user || 0)}</div>
                  <div className="p99-label">API Calls</div>
                </div>
                <div className="p99-card">
                  <h4>P99 Latency</h4>
                  <div className="p99-value">{usageAnalytics.p99_stats?.latency_p99 || 0}ms</div>
                  <div className="p99-label">Response Time</div>
                </div>
              </div>
            </div>

            {/* Endpoint Usage Trends */}
            {usageAnalytics.endpoint_trends && usageAnalytics.endpoint_trends.length > 0 && (
              <div className="analytics-section">
                <h3>Endpoint Usage Trends</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={usageAnalytics.endpoint_trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {Object.keys(usageAnalytics.endpoint_trends[0] || {})
                      .filter(key => key !== 'date')
                      .slice(0, 5)
                      .map((endpoint, index) => (
                        <Line 
                          key={endpoint} 
                          type="monotone" 
                          dataKey={endpoint} 
                          stroke={`hsl(${index * 60}, 70%, 50%)`}
                          strokeWidth={2}
                        />
                      ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Tier Behavior Comparison */}
            {usageAnalytics.tier_comparison && (
              <div className="analytics-section">
                <h3>Tier Behavior Comparison</h3>
                <div className="tier-comparison-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Tier</th>
                        <th>API Calls</th>
                        <th>Models Created</th>
                        <th>Datasets Uploaded</th>
                        <th>Avg per User</th>
                        <th>User Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(usageAnalytics.tier_comparison).map(([tier, data]) => (
                        <tr key={tier}>
                          <td>{tier.charAt(0).toUpperCase() + tier.slice(1)}</td>
                          <td>{data.api_calls || 0}</td>
                          <td>{data.models_created || 0}</td>
                          <td>{data.datasets_uploaded || 0}</td>
                          <td>{Math.round(data.avg_per_user || 0)}</td>
                          <td>{data.user_count || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={Object.entries(usageAnalytics.tier_comparison).map(([tier, data]) => ({
                    tier: tier.charAt(0).toUpperCase() + tier.slice(1),
                    'API Calls': data.api_calls || 0,
                    'Models Created': data.models_created || 0,
                    'Datasets Uploaded': data.datasets_uploaded || 0
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="tier" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="API Calls" fill="#8884d8" />
                    <Bar dataKey="Models Created" fill="#82ca9d" />
                    <Bar dataKey="Datasets Uploaded" fill="#ffc658" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Key Actions Usage */}
            {usageAnalytics.key_actions && usageAnalytics.key_actions.length > 0 && (
              <div className="analytics-section">
                <h3>Key Actions Usage</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={usageAnalytics.key_actions} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="endpoint" type="category" width={200} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Model Type Breakdown */}
            {usageAnalytics.model_type_breakdown && (
              <div className="analytics-section">
                <h3>Model Type Breakdown</h3>
                <div className="model-type-stats">
                  {Object.entries(usageAnalytics.model_type_breakdown).map(([type, data]) => (
                    <div key={type} className="model-type-card">
                      <h4>{type.charAt(0).toUpperCase() + type.slice(1)}</h4>
                      <div className="model-type-value">Models: {data.count || 0}</div>
                      <div className="model-type-value">API Calls: {Math.round(data.api_calls || 0)}</div>
                      <div className="model-type-percentage">{formatPercentage(data.percentage || 0)}</div>
                    </div>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={Object.entries(usageAnalytics.model_type_breakdown).map(([type, data]) => ({
                        name: type.charAt(0).toUpperCase() + type.slice(1),
                        value: data.api_calls || 0
                      }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {Object.entries(usageAnalytics.model_type_breakdown).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={`hsl(${index * 120}, 70%, 50%)`} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>

      {/* Generated At */}
      <div className="analytics-footer">
        <small>
          Last updated: {analytics.generated_at ? new Date(analytics.generated_at).toLocaleString() : 'Unknown'}
        </small>
      </div>

      <style jsx>{`
        .subscription-analytics {
          padding: 20px;
          background: var(--bg-primary, #f8f9fa);
          min-height: 100vh;
        }

        .analytics-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid var(--border-color, #e9ecef);
        }

        .analytics-header h2 {
          margin: 0;
          color: var(--text-primary, #333);
        }

        .header-actions {
          display: flex;
          gap: 15px;
          align-items: center;
        }

        .refresh-button, .export-button {
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .refresh-button {
          background: var(--primary-color, #007bff);
          color: white;
        }

        .refresh-button:hover:not(:disabled) {
          background: var(--primary-hover, #0056b3);
        }

        .export-button {
          background: var(--success-color, #28a745);
          color: white;
        }

        .export-button.json {
          background: var(--info-color, #17a2b8);
        }

        .export-button:hover:not(:disabled) {
          opacity: 0.9;
        }

        .export-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .metric-card {
          background: white;
          padding: 20px;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          transition: transform 0.2s ease;
        }

        .metric-card:hover {
          transform: translateY(-2px);
        }

        .metric-content h3 {
          margin: 0 0 5px 0;
          color: var(--text-secondary, #666);
          font-size: 14px;
          font-weight: 500;
        }

        .metric-value {
          font-size: 24px;
          font-weight: bold;
          color: var(--text-primary, #333);
          margin-bottom: 5px;
        }

        .metric-subtitle {
          font-size: 12px;
          color: var(--text-muted, #999);
        }

        .analytics-section {
          background: white;
          padding: 20px;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          margin-bottom: 20px;
        }

        .analytics-section h3 {
          margin: 0 0 20px 0;
          color: var(--text-primary, #333);
        }

        .plan-distribution {
          margin-bottom: 20px;
        }

        .plan-bar {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 15px;
        }

        .plan-label {
          display: flex;
          justify-content: space-between;
          min-width: 150px;
        }

        .plan-name {
          font-weight: 500;
          color: var(--text-primary, #333);
        }

        .plan-count {
          color: var(--text-secondary, #666);
          font-size: 14px;
        }

        .plan-progress {
          flex: 1;
          height: 20px;
          background: var(--light-bg, #f8f9fa);
          border-radius: 10px;
          overflow: hidden;
        }

        .plan-fill {
          height: 100%;
          background: linear-gradient(90deg, #007bff, #28a745);
          transition: width 0.3s ease;
        }

        .plan-percentage {
          min-width: 60px;
          text-align: right;
          font-weight: 500;
          color: var(--text-primary, #333);
        }

        .revenue-breakdown {
          margin-bottom: 20px;
        }

        .revenue-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 0;
          border-bottom: 1px solid var(--border-color, #e9ecef);
        }

        .revenue-item:last-child {
          border-bottom: none;
        }

        .revenue-plan {
          font-weight: 500;
          color: var(--text-primary, #333);
        }

        .revenue-amount {
          font-weight: bold;
          color: var(--success-color, #28a745);
        }

        .status-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 20px;
        }

        .status-item {
          text-align: center;
          padding: 15px;
          background: var(--light-bg, #f8f9fa);
          border-radius: 8px;
        }

        .status-name {
          font-size: 14px;
          color: var(--text-secondary, #666);
          margin-bottom: 5px;
        }

        .status-count {
          font-size: 20px;
          font-weight: bold;
          color: var(--text-primary, #333);
        }

        .analytics-footer {
          text-align: center;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid var(--border-color, #e9ecef);
          color: var(--text-muted, #999);
        }

        .loading, .error, .no-data {
          text-align: center;
          padding: 40px;
          color: var(--text-secondary, #666);
        }

        .error {
          color: var(--danger-color, #dc3545);
        }

        .retry-button {
          margin-top: 10px;
          padding: 8px 16px;
          background: var(--primary-color, #007bff);
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
        }

        @media (max-width: 768px) {
          .analytics-header {
            flex-direction: column;
            gap: 15px;
            align-items: stretch;
          }

          .header-actions {
            justify-content: center;
          }

          .metrics-grid {
            grid-template-columns: 1fr;
          }

          .plan-bar {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          }

          .plan-label {
            min-width: auto;
          }

          .plan-percentage {
            text-align: left;
          }
        }

        .usage-analytics {
          margin-top: 40px;
          padding-top: 30px;
          border-top: 2px solid var(--border-color, #e9ecef);
        }

        .filters-section {
          display: flex;
          gap: 20px;
          align-items: flex-end;
          margin-bottom: 30px;
          padding: 20px;
          background: white;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
          flex: 1;
        }

        .filter-group label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-secondary, #666);
        }

        .filter-select {
          padding: 8px 12px;
          border: 1px solid var(--border-color, #e9ecef);
          border-radius: 6px;
          font-size: 14px;
          background: white;
        }

        .p99-stats-section {
          margin-bottom: 30px;
        }

        .p99-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }

        .p99-card {
          background: white;
          padding: 20px;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          text-align: center;
        }

        .p99-card h4 {
          margin: 0 0 10px 0;
          color: var(--text-secondary, #666);
          font-size: 14px;
          font-weight: 500;
        }

        .p99-value {
          font-size: 32px;
          font-weight: bold;
          color: var(--primary-color, #007bff);
          margin-bottom: 5px;
        }

        .p99-label {
          font-size: 12px;
          color: var(--text-muted, #999);
        }

        .tier-comparison-table {
          margin-bottom: 30px;
          overflow-x: auto;
        }

        .tier-comparison-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        .tier-comparison-table th,
        .tier-comparison-table td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid var(--border-color, #e9ecef);
        }

        .tier-comparison-table th {
          background: var(--light-bg, #f8f9fa);
          font-weight: 600;
          color: var(--text-primary, #333);
        }

        .tier-comparison-table tr:hover {
          background: var(--light-bg, #f8f9fa);
        }

        .model-type-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .model-type-card {
          background: white;
          padding: 20px;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          text-align: center;
        }

        .model-type-card h4 {
          margin: 0 0 15px 0;
          color: var(--text-primary, #333);
          font-size: 18px;
        }

        .model-type-value {
          font-size: 16px;
          color: var(--text-secondary, #666);
          margin-bottom: 8px;
        }

        .model-type-percentage {
          font-size: 24px;
          font-weight: bold;
          color: var(--primary-color, #007bff);
          margin-top: 10px;
        }

        @media (max-width: 768px) {
          .filters-section {
            flex-direction: column;
            align-items: stretch;
          }

          .p99-cards,
          .model-type-stats {
            grid-template-columns: 1fr;
          }

          .tier-comparison-table {
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default SubscriptionAnalytics;
